import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/pages/first-principles.css";

/* ============================================================
   "The First Principles of Hiring" — long-form essay.
   Deliberately its own editorial register (Newsreader + IBM
   Plex, warm cream, cyan accent) distinct from the rest of the
   marketing site — see Styles/pages/first-principles.css.
   Site header/footer wrap this like every other route; the
   page owns only its body content and the two share affordances
   the original design gave it.
   ============================================================ */

export default function FirstPrinciples() {
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2200);
  }

  function share() {
    const url = window.location.href;
    const data = {
      title: "The First Principles of Hiring",
      text:
        "Every hiring process is exactly as good as its definition of success — and no better.",
      url,
    };
    if (navigator.share) {
      navigator.share(data).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(
        () => showToast("Link copied to clipboard"),
        () => showToast(url)
      );
    } else {
      showToast(url);
    }
  }

  return (
    <div className="fp">
      {/* MOVEMENT I · opening */}
      <section className="opening" aria-labelledby="fp-h1">
        <div className="body">
          <p className="kicker">A ten-minute argument</p>
          <h1 id="fp-h1">The First Principles of Hiring</h1>
          <p className="lede">
            No one hires for the sake of hiring. A role opens because
            something must be achieved — revenue that isn&rsquo;t growing on
            its own, a function that needs an owner, a problem that will not
            solve itself — and because someone decided that a new person is
            the right way to achieve it.
          </p>
        </div>
      </section>

      <section className="opening-cont">
        <div className="body">
          <p className="pull tight">
            Before there was a vacancy, there was an outcome.
          </p>
          <p className="copy">
            Which makes every hire a prediction. Someone looked at the
            evidence available and predicted that this person, of all the
            people considered, would produce those outcomes. The offer
            letter is the wager.
          </p>
          <p className="pull small">
            It is worth asking how that prediction is usually made.
          </p>
        </div>
      </section>

      {/* MOVEMENT II */}
      <section className="movement" aria-labelledby="fp-m2">
        <div className="body">
          <span className="numeral">II</span>
          <h2 id="fp-m2">Thirty Years of Faster</h2>
          <p className="copy">
            Over the last thirty years, nearly every part of hiring has been
            rebuilt. Job boards replaced newspaper columns. Applicant
            tracking systems replaced filing cabinets. LinkedIn made half
            the working world searchable from a desk. Now AI reads résumés
            in milliseconds and drafts outreach by the thousand.
          </p>
          <p className="copy">
            Each of these made hiring faster. Some made it wider. None of
            them, by any honest account, made its predictions more
            reliable. Ask any leader who has hired for twenty years whether
            hiring feels more certain than it did in the nineties, and you
            will get a wry look.
          </p>
          <p className="copy">
            That is a strange kind of progress: three decades of
            technological improvement without conceptual improvement. Speed
            is what progress looks like when nobody has defined what better
            means.
          </p>
          <p className="pull">
            We improved every step of the process without ever asking what
            the process was for.
          </p>
        </div>
      </section>

      {/* MOVEMENT III */}
      <section className="movement" aria-labelledby="fp-m3">
        <div className="body">
          <span className="numeral">III</span>
          <h2 id="fp-m3">The Lost Standard</h2>
          <p className="copy">
            Go back to the moment a role opens. At that moment, the
            standard exists. The hiring manager knows — often precisely —
            what the new person must produce, because that need is the
            reason the role exists at all.
          </p>
          <p className="copy">
            Then the translation begins. The outcome is translated into a
            job description: a list of requirements, responsibilities, and
            qualifications. The job description is translated into search
            keywords. The keywords surface résumés, and the résumés are
            translated into impressions — a strong brand here, a familiar
            title there. Each translation loses something, and it always
            loses the same thing: the reason the role exists. By the time a
            candidate sits in an interview, they are being measured against
            a photocopy of a photocopy of the outcome — and nobody in the
            room has seen the original.
          </p>
          <p className="copy">
            The instruments themselves make this loss easy to miss, because
            each one measures <em>something</em>. It is just never quite
            the right thing.
          </p>
        </div>
      </section>

      {/* CLEARING ONE · THE THEOREM */}
      <section className="theorem" aria-label="The theorem">
        <div className="inner">
          <div className="grid">
            <span className="l">Requirements</span>
            <span className="ne">&#8800;</span>
            <span className="r">Success</span>
            <span className="l">Responsibilities</span>
            <span className="ne">&#8800;</span>
            <span className="r">Outcomes</span>
            <span className="l">Qualifications</span>
            <span className="ne">&#8800;</span>
            <span className="r">Capability</span>
            <span className="l">Experience</span>
            <span className="ne">&#8800;</span>
            <span className="r">Evidence</span>
          </div>
          <div className="mark" aria-hidden="true">
            <span className="tile" />
            <span className="wm">Ann</span>
          </div>
        </div>
      </section>

      <section className="cont">
        <div className="body">
          <p className="copy">
            And into the space left by the missing standard, something
            predictable moves. When standards are unclear, judgment fills
            the gap. This is not a failure of intelligence or of intent —
            it is what judgment does. It is why interviews drift into
            chemistry tests, why a famous employer on a résumé outweighs an
            unglamorous decade of results, why five interviewers reach five
            verdicts about the same person, and why bias finds room to
            work. The standard was never written down, and everyone in the
            process is quietly improvising their own.
          </p>
          <p className="copy">
            You have sat in the room where this ends. The scores are
            inconclusive, the panel is split, and the decision finally
            arrives as a sentence everyone in hiring recognises:{" "}
            <em>&ldquo;I have a good feeling about her.&rdquo;</em> Careers
            turn on that sentence. So do companies.
          </p>
          <p className="copy">Which leaves the only question that matters:</p>
        </div>
      </section>

      {/* THE QUESTION */}
      <section className="question" aria-label="The central question">
        <div className="inner">
          <p>What exactly are we measuring candidates against?</p>
        </div>
      </section>

      {/* MOVEMENT IV */}
      <section className="movement" aria-labelledby="fp-m4">
        <div className="body">
          <span className="numeral">IV</span>
          <h2 id="fp-m4">Define Success First</h2>
          <p className="copy">
            The answer, in most hiring processes, is: nothing in
            particular. A JD written for a job portal. A hazy composite in
            each interviewer&rsquo;s head. A feeling that firms up as the
            meeting goes well.
          </p>
          <p className="copy">
            Most hiring managers will say the standard exists — it is right
            there in the job description. But read one honestly: it lists
            what the person must have and what they will be responsible
            for. Inputs and duties. Very rarely does it state what the role
            must produce, or what evidence would show that a particular
            candidate can produce it.
          </p>
          <p className="copy">
            The correction is not a new technology. It is a discipline
            older than any of the tools: define the standard before you
            measure. In any other field, measuring against an undefined
            standard would be unthinkable — no laboratory calibrates an
            instrument against a feeling. In hiring, it is the norm. Write
            down, before anyone is searched for or screened or interviewed,
            what this role must produce — the outcomes it exists to
            achieve, the capabilities that produce those outcomes, the
            things that separate a person who will succeed here from a
            person who merely qualifies.
          </p>
          <p className="copy">
            This is not an innovation. It is a recovery — of the lost
            standard, the one that existed the day the role was opened.
            Defining success first simply means refusing to let it
            evaporate on the way to the interview. And once it is written
            down, something changes in the nature of every step that
            follows. Sourcing, screening, and interviewing stop being acts
            of judgment and become acts of measurement.
          </p>
          <p className="copy">
            You cannot evaluate a candidate against a standard that was
            never defined.
          </p>
          <p className="pull">
            Every hiring process is exactly as good as its definition of
            success — and no better.
          </p>
        </div>
      </section>

      {/* MOVEMENT V */}
      <section className="movement" aria-labelledby="fp-m5">
        <div className="body">
          <span className="numeral">V</span>
          <h2 id="fp-m5">Success, Written Down</h2>
          <p className="copy">
            If the discipline is that simple, it is fair to ask why almost
            nobody practises it. The honest answer: because doing it
            rigorously is hard. Defining success for a role — properly, not
            as a slogan — means thinking through what the business actually
            needs, which capabilities demonstrably produce it, and what
            observable evidence would separate the genuinely capable from
            the well-rehearsed. It is hours of structured thinking per
            role, and it is the first thing abandoned when a requisition is
            urgent. It is the work everyone agrees matters and nobody has
            time to do.
          </p>
          <p className="ann-line" id="ann-entrance">
            <span className="dot" aria-hidden="true" />
            It is also the work <em>Ann</em> was built to do first.
          </p>
          <p className="copy">
            Before any search begins, before any résumé is read, before any
            interview is scheduled, the standard is defined and written
            down. The definition is called a RoleDNA — not a summary of
            the job description, but the standard the job description
            failed to preserve, recovered.
          </p>
        </div>
      </section>

      {/* CLEARING TWO · THE BLUEPRINT */}
      <section className="blueprint" aria-label="A RoleDNA example">
        <div className="inner">
          <figure>
            <p className="tag">
              ROLEDNA — BUSINESS DEVELOPMENT SPECIALIST, EARLY-STAGE SAAS
            </p>
            <p className="subtag">
              (one of its Critical Success Factors, as delivered)
            </p>
            <p className="csf">
              <span className="n">CSF 02</span> — Discovery-led demo that
              converts interest into a committed next step.
            </p>
            <p className="csf-body">
              Runs a demo that begins with workflow discovery — the
              buyer&rsquo;s current screening and interview steps, the
              systems in use, the bottlenecks, the success metrics — and
              then shows only what maps to what was heard, earning a
              concrete next step rather than a polite ending.
            </p>
            <div className="cols">
              <div className="col strong">
                <p className="lbl">STRONG LOOKS LIKE</p>
                <p>
                  asks specific workflow questions before sharing a screen,
                  tailors the demo path, names what would make the product
                  a yes or a no, and closes with a scheduled next step and
                  a stakeholder plan.
                </p>
              </div>
              <div className="col">
                <p className="lbl">WEAK LOOKS LIKE</p>
                <p>
                  jumps straight into a feature tour, cannot connect the
                  product to the buyer&rsquo;s process, leaves the call
                  with &ldquo;we&rsquo;ll get back to you,&rdquo; and treats
                  the demo as the finish line.
                </p>
              </div>
            </div>
            <figcaption>
              <em>Anatomy of a RoleDNA:</em> Success Milestones &middot;
              Execution Capabilities &middot; Critical Success Factors
              &middot; Success Enablers &middot; Hiring Signals
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="cont tight">
        <div className="body">
          <p className="copy">
            Read the two columns again. They are not requirements, not
            competencies, not keywords. They are descriptions of evidence —
            the observable difference between a person who has this
            capability and a person who merely talks like one. And notice
            the uncomfortable part: the weak column is not describing a bad
            candidate. It is often describing the impressive one. That is
            the difference between asking whether a candidate{" "}
            <em>sounds</em> right and knowing what would <em>prove</em> it.
          </p>
          <p className="pull italic">
            Most people who see a RoleDNA for a role they know have the
            same reaction: I have hired for this role three times, and I
            have never once seen its success written down.
          </p>
        </div>
      </section>

      {/* MOVEMENT VI */}
      <section className="movement" aria-labelledby="fp-m6">
        <div className="body">
          <span className="numeral">VI</span>
          <h2 id="fp-m6">One Prediction</h2>
          <p className="copy">Everything downstream inherits the definition.</p>
          <p className="copy">
            Search stops being a keyword exercise and becomes design: it
            looks for people in whom evidence of <em>this</em> success
            could plausibly exist — in adjacent companies, in unfamiliar
            titles, in places a keyword search structurally cannot reach.
            The same standard now governs every decision.
          </p>
          <p className="copy">
            Résumés stop being impressions and become documents to be read
            for evidence — claims weighed against the definition, strengths
            credited, gaps recorded rather than glossed. The interview
            stops being a conversation that goes well or badly and becomes
            verification: structured, evidence-scored, asking for precisely
            the proof the standard demands. The same standard now governs
            every decision.
          </p>
          <p className="copy">
            And this is the part that matters more than any single stage.
            Each stage is no longer a ritual to be completed. Each one
            gathers evidence for the same prediction —{" "}
            <em>this person will produce these outcomes</em> — stated
            once, at the start, in the definition of success. Search
            gathers it. The résumé evaluation weighs it. The interview
            tests it. The recommendation assembles it.
          </p>
          <p className="pull mid">
            Not six stages. One prediction, and the same standard governing
            every decision made about it.
          </p>
          <p className="copy">
            That is the entire innovation, and it is conceptual before it
            is technological. Which is why no amount of faster tooling
            ever produced it.
          </p>
        </div>
      </section>

      {/* MOVEMENT VII */}
      <section className="movement" aria-labelledby="fp-m7">
        <div className="body">
          <span className="numeral">VII</span>
          <h2 id="fp-m7">Where Evidence Ends</h2>
          <p className="copy">
            What arrives at the end is a recommendation you can hold. A
            shortlist — not a longlist — and for each person on it: what
            the evidence says, where it came from, how strong it is, and
            what it does not cover.
          </p>
          <p className="copy">
            The feeling this produces is confidence — but it is worth
            being precise about the direction. The confidence is not
            supplied; it is earned, and it decomposes. When a CFO asks why
            this candidate, the answer is not a score. It is the reasoning
            under the score: this claim, from this response, weighed
            against this factor of the standard. Confidence comes from
            evidence. It does not work the other way around — and every
            hiring process that starts from confidence and works backward
            to justification has already failed.
          </p>
          <p className="copy">
            Honesty requires saying the rest plainly. Evidence is
            powerful. Evidence is not omniscience.
          </p>
          <p className="copy">
            No definition of success, however rigorous, makes the future
            certain. A candidate with strong evidence can still fail —
            circumstances change, organisations change, people change.
            What evidence can do is different, and better than what it
            replaces: it can state how strong the prediction is and show
            exactly what it rests on. Where the evidence is thin, the
            report says so. Where a capability could not be verified, the
            gap is admitted, not smoothed over — and confidence in the
            evaluation itself is stated separately from the candidate&rsquo;s
            score, because those are two different things, and merging
            them is how numbers learn to lie.
          </p>
          <p className="pull wide">
            A prediction that knows its own limits is worth more than a
            certainty that doesn&rsquo;t. If a page like this one promised
            you certainty, you would be right not to trust the rest of it.
          </p>
        </div>
      </section>

      {/* MOVEMENT VIII · dark finale + CTA */}
      <section className="finale" aria-labelledby="fp-m8">
        <div className="body">
          <span className="numeral">VIII</span>
          <h2 id="fp-m8">One Role</h2>
          <p className="copy">
            Every hire you will ever make is a prediction. That was true
            before this page, and it will be true after. The only question
            is what your predictions are built on.
          </p>
          <p className="copy">
            Before there was a vacancy, there was an outcome. Before your
            next one, there can be a standard.
          </p>
          <p className="copy">
            There is one way to see the difference, and it takes a day, not
            a meeting. Send Ann a role — a live one, one you know well.
            Read what comes back.
          </p>
          <p className="copy">
            You will know within a page whether it is right. You have
            lived this role — you are the one reader it cannot bluff.
          </p>
          <p className="pull">See what success looks like before hiring begins.</p>
          <div className="ctas">
            <Link to="/send-a-role" className="btn-cta">
              Send Ann a role <span className="arr">&#8594;</span>
            </Link>
            <button type="button" className="btn-share" onClick={share}>
              Share this argument <span className="arr">&#8599;</span>
            </button>
          </div>
        </div>
      </section>

      <button
        type="button"
        className="quiet-share"
        onClick={share}
        aria-label="Share this page"
      >
        Share <span className="arr">&#8599;</span>
      </button>

      {toast ? (
        <div className="toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
